import { useCallback, useState } from "react";
import type { Venue } from "../api/types";
import { Field } from "../ui/components";
import { useVenueScope } from "./venueScope";

// The venue question for the two pages allowed to answer it with "every venue". Everywhere else the
// top bar decides and a page simply reads selectedVenueId. Finance and Analytics are where
// management goes for the whole picture, so those two may widen to every venue this person can
// see, as a choice made on the page and forgotten when it closes. The top bar still names one
// venue throughout: widening is a way of looking at money and numbers, not a second scope the rest
// of the product has to know about. Somebody assigned to a single venue is never offered the
// choice, because there is nothing to widen to.
export interface VenueFilter {
  // Empty means every venue this person may see, and is only ever empty when canWiden is true.
  venueId: string;
  venues: Venue[];
  // False while the venue list is loading. Queries keyed on venueId should wait for it, or a
  // single-venue person briefly asks for everything and then again for their venue.
  ready: boolean;
  canWiden: boolean;
  wide: boolean;
  label: string;
  setVenueId(venueId: string): void;
}

export function useVenueFilter(): VenueFilter {
  const scope = useVenueScope();
  const [wide, setWide] = useState(false);
  const canWiden = scope.venues.length > 1;
  const widened = canWiden && wide;
  const { setVenueId: choose } = scope;
  const setVenueId = useCallback(
    (venueId: string) => {
      if (venueId === "") {
        setWide(true);
        return;
      }
      // Picking a venue here is the same act as picking it in the top bar, so the two never
      // disagree about which venue somebody is looking at.
      setWide(false);
      choose(venueId);
    },
    [choose],
  );
  return {
    venueId: widened ? "" : scope.selectedVenueId,
    venues: scope.venues,
    ready: !scope.loading,
    canWiden,
    wide: widened,
    label: widened ? "All venues" : scope.label,
    setVenueId,
  };
}

// The control for the pages that may widen. Somebody with one venue is shown nothing at all: the
// top bar already names it, and a select with a single option suggests a choice that is not there.
export function VenueFilterSelect({
  filter,
  label = "Venue",
}: {
  filter: VenueFilter;
  label?: string | undefined;
}) {
  if (!filter.canWiden) return null;
  return (
    <Field label={label}>
      <select value={filter.venueId} onChange={(event) => filter.setVenueId(event.target.value)}>
        {filter.venues.map((venue) => (
          <option key={venue.id} value={venue.id}>
            {venue.name}
          </option>
        ))}
        <option value="">All venues</option>
      </select>
    </Field>
  );
}
